import React from 'react';
import styled from 'styled-components';
import { Note, TypeOfNote } from '@annote/types';
import { colors, text } from '@annote/core/styles';
import { FileText } from 'lucide-react';

const Container = styled.span<{ noteType: TypeOfNote; $maxWidth?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  margin-right: 4px;
  background: #ffffff;
  border: 1px solid ${colors.borders.input.primary};
  border-radius: 4px;
  font-family: ${text.family.primary};
  font-size: ${text.size.small};
  color: ${colors.text.primary};
  cursor: pointer;
  transition: background 0.2s;
  vertical-align: baseline;
  line-height: 1.2;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}

  &:hover {
    background: ${colors.bg.hover};
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${colors.text.primary};
  opacity: 0.8;

  svg {
    color: ${colors.text.primary};
    fill: none;
    stroke: currentColor;
  }
`;

const NoteName = styled.span<{ $truncate?: boolean }>`
  font-weight: ${text.weight.medium};
  ${({ $truncate }) =>
    $truncate &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const TextReference = styled.span`
  color: ${colors.text.description};
  font-size: ${text.size.small};
  margin-left: 4px;
  font-style: italic;
`;

// Simple card icon SVG (for takeaway/annote)
const CardIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="1" y1="5" x2="13" y2="5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const getIconForNoteType = (noteType: TypeOfNote, size: number = 14): React.ReactNode => {
  switch (noteType) {
    case 'source':
      return <FileText size={size} strokeWidth={1.5} />;
    case 'takeaway':
    case 'annote':
    default:
      return <CardIcon size={size} />;
  }
};

interface InlineNoteProps {
  note: Note | { id: string; title: string; type: TypeOfNote };
  textReference?: string;
  textStart?: number;
  textEnd?: number;
  /** Max width (e.g. "150px") - enables truncation with ellipsis */
  maxWidth?: string;
}

export const InlineNote: React.FC<InlineNoteProps> = ({ note, textReference, textStart, textEnd, maxWidth }) => {
  const noteName = note.title || 'Untitled Note';
  const showTextRef = textReference && textStart !== undefined && textEnd !== undefined;

  return (
    <Container noteType={note.type} $maxWidth={maxWidth} title={noteName}>
      <IconWrapper>{getIconForNoteType(note.type, 12)}</IconWrapper>
      <NoteName $truncate={!!maxWidth}>{noteName}</NoteName>
      {showTextRef && <TextReference>{` (${textStart}-${textEnd})`}</TextReference>}
    </Container>
  );
};

export default InlineNote;
